import React, { useState, forwardRef, useRef } from 'react';
import PropTypes from 'prop-types';
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';
import Input from 'react-phone-number-input/input';
import { Alert, Button, Col, Form, OverlayTrigger, Popover, Tooltip } from 'react-bootstrap';
import DatePicker, { registerLocale } from 'react-datepicker';
import SimpleBarReact from 'simplebar-react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import AsyncCreatableSelect from 'react-select/async-creatable';
import es from 'date-fns/locale/es';
import { v4 as uuid } from 'uuid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from './Flex';
import classNames from 'classnames';
import FalconReactRange from 'components/doc-components/FalconReactRange';
import { isIterableArray } from 'helpers/utils';
registerLocale('es', es);

const CustomInput = forwardRef(({ value, onChange, isInvalid, isValid, errorMessage, size }, ref) => {
 return (
  <>
   <Form.Control ref={ref} size={size} value={value} isValid={isValid} isInvalid={isInvalid} onChange={onChange} />
   {errorMessage && <Form.Control.Feedback type="invalid">{errorMessage}</Form.Control.Feedback>}
  </>
 );
});

const CustomDateInput = forwardRef(({ value, onClick, isInvalid, isValid, formControlProps, errorMessage }, ref) => {
 return (
  <div className="position-relative">
   <Form.Control
    ref={ref}
    isInvalid={isInvalid}
    isValid={isValid}
    value={value}
    onClick={onClick}
    onChange={e => console.log('no esta pasando nada ', e)}
    {...formControlProps}
   />
   <FontAwesomeIcon icon="calendar-alt" className="text-primary position-absolute top-50 translate-middle-y ms-2" />
   {errorMessage && <Form.Control.Feedback type="invalid">{errorMessage}</Form.Control.Feedback>}
  </div>
 );
});

const Inputs = ({
 help = null,
 label,
 name,
 errors,
 type = 'text',
 options = [],
 placeholder,
 formControlProps,
 formGroupProps,
 formLabelProps,
 formColumnProps,
 datepickerProps
}) => {
 const [date, setDate] = useState(new Date());
 const [item, setItem] = useState({ id: null, value: null });
 const itemRef = useRef();

 const getNestedPropertyValue = (errors, name) => {
  if (name.indexOf('.') >= 0) return name.split('.').reduce((acc, prop) => (acc?.[prop] ? acc[prop] : false), errors);
  if (!isIterableArray(name)) return errors[name];
  return name.reduce((acc, prop) => (acc?.[prop] ? acc[prop] : false), errors);
 };

 const handleChange = ({ target: { value } }) => setItem({ ...item, value });

 const handleReset = () => {
  setItem({ id: null, value: null });
  itemRef.current.value = null;
 };

 const handleSubmit = () => {
  const list = !item.id ? [...options, { id: uuid(), value: item?.value }] : options.map(e => (e.id === item?.id ? item : e));
  handleReset();
  return list;
 };

 //const onKeyDown = key => key.keyCode == 13 && handleSubmit();

 const Label = help ? (
  <>
   {label}
   <OverlayTrigger
    trigger="click"
    placement="auto"
    overlay={
     <Popover id={uuid()}>
      {help?.title && <Popover.Header as="h3">{help.title}</Popover.Header>}
      <Popover.Body>{help.body}</Popover.Body>
     </Popover>
    }
   >
    <Button variant="link" type="button" className="text-700 p-0 ms-1">
     <FontAwesomeIcon icon="question-circle" className="fs--1" />
    </Button>
   </OverlayTrigger>
  </>
 ) : (
  label
 );

 if (type === 'date') {
  return (
   <Form.Group {...formGroupProps}>
    {!!label && <Form.Label {...formLabelProps}>{Label}</Form.Label>}
    <Col
     {...formColumnProps}
     children={
      <DatePicker
       {...formControlProps}
       customInput={
        <CustomDateInput
         formControlProps={formControlProps}
         errorMessage={getNestedPropertyValue(errors, name)?.message}
         isInvalid={!!getNestedPropertyValue(errors, name)}
         isValid={Object.keys(errors).length > 0 && !getNestedPropertyValue(errors, name)}
        />
       }
       {...datepickerProps}
      />
     }
    />
   </Form.Group>
  );
 }

 if (type === 'simple-date2') {
  return (
   <Form.Group {...formGroupProps}>
    <DatePicker
     selected={date}
     onChange={date => {
      console.log(date);
      setDate(date);
     }}
     {...datepickerProps}
     customInput={
      <CustomDateInput
       formControlProps={formControlProps}
       errorMessage={getNestedPropertyValue(errors, name)?.message}
       isInvalid={!!getNestedPropertyValue(errors, name)}
       isValid={Object.keys(errors).length > 0 && !getNestedPropertyValue(errors, name)}
      />
     }
    />
   </Form.Group>
  );
 }

 if (type === 'simple-date') {
  return (
   <Form.Group {...formGroupProps}>
    {!!label && <Form.Label {...formLabelProps}>{Label}</Form.Label>}
    <Col
     {...formColumnProps}
     children={
      <>
       <Form.Control
        type={type.split('-')[1]}
        placeholder={placeholder}
        {...formControlProps}
        isInvalid={getNestedPropertyValue(errors, name)}
        isValid={Object.keys(errors).length > 0 && !getNestedPropertyValue(errors, name)}
       />
       {getNestedPropertyValue(errors, name)?.message && (
        <Form.Control.Feedback type="invalid">{getNestedPropertyValue(errors, name).message}</Form.Control.Feedback>
       )}
      </>
     }
    />
   </Form.Group>
  );
 }

 if (type === 'checkbox' || type === 'switch' || type === 'radio') {
  return (
   <Form.Check type={type} id={name + Math.floor(Math.random() * 100)} {...formGroupProps}>
    <Form.Check.Input
     type={type}
     {...formControlProps}
     isInvalid={getNestedPropertyValue(errors, name)}
     isValid={Object.keys(errors).length > 0 && !getNestedPropertyValue(errors, name)}
    />
    <Form.Check.Label {...formLabelProps}>{label}</Form.Check.Label>
    {getNestedPropertyValue(errors, name)?.message && (
     <Form.Control.Feedback type="invalid" className="mt-0">
      {getNestedPropertyValue(errors, name).message}
     </Form.Control.Feedback>
    )}
   </Form.Check>
  );
 }

 if (type === 'selectMulti2') {
  return (
   <Form.Group {...formGroupProps}>
    {!!label && <Form.Label {...formLabelProps}>{Label}</Form.Label>}
    <Col
     {...formColumnProps}
     children={
      <>
       <CreatableSelect
        isInvalid={getNestedPropertyValue(errors, name)}
        isValid={Object.keys(errors).length > 0 && !getNestedPropertyValue(errors, name)}
        placeholder={placeholder}
        options={options}
        {...formControlProps}
       />
       {getNestedPropertyValue(errors, name)?.message && (
        <Form.Text className="text-danger">{getNestedPropertyValue(errors, name)?.message}</Form.Text>
       )}
      </>
     }
    />
   </Form.Group>
  );
 }

 if (type === 'selectMultiFixed') {
  return (
   <Form.Group {...formGroupProps}>
    {!!label && <Form.Label {...formLabelProps}>{Label}</Form.Label>}
    <Col
     {...formColumnProps}
     children={
      <>
       {/* defaultValue={[options[2], options[3]]} */}
       <Select placeholder={placeholder} options={options} {...formControlProps} />
       <Form.Text className="text-danger position-relative">
        {errors && getNestedPropertyValue(errors, name)?.message ? (
         <>
          <FontAwesomeIcon
           icon="exclamation-circle"
           className="text-white fs-1 bg-warning rounded-circle position-absolute top-50 translate-middle-y"
          />
          <span className="ms-4">{getNestedPropertyValue(errors, name)?.message}!</span>
         </>
        ) : (
         ''
        )}
       </Form.Text>
      </>
     }
    />
   </Form.Group>
  );
 }

 if (type === 'selectMultiOnline') {
  return (
   <Form.Group {...formGroupProps}>
    {!!label && <Form.Label {...formLabelProps}>{Label}</Form.Label>}
    <Col
     {...formColumnProps}
     children={
      <>
       <AsyncCreatableSelect cacheOptions defaultOptions={options} {...formControlProps} />
       <Form.Text className="text-danger position-relative">
        {errors && getNestedPropertyValue(errors, name)?.message ? (
         <>
          <FontAwesomeIcon
           icon="exclamation-circle"
           className="text-white fs-1 bg-warning rounded-circle position-absolute top-50 translate-middle-y"
          />
          <span className="ms-4">{getNestedPropertyValue(errors, name)?.message}!</span>
         </>
        ) : (
         ''
        )}
       </Form.Text>
      </>
     }
    />
   </Form.Group>
  );
 }

 if (type === 'selectMulti') {
  return (
   <Form.Group {...formGroupProps}>
    {!!label && <Form.Label {...formLabelProps}>{Label}</Form.Label>}
    <Col
     {...formColumnProps}
     children={
      <>
       <CreatableSelect placeholder={placeholder} options={options} {...formControlProps} />
       <Form.Text className="text-danger position-relative">
        {errors && getNestedPropertyValue(errors, name)?.message ? (
         <>
          <FontAwesomeIcon
           icon="exclamation-circle"
           className="text-white fs-1 bg-warning rounded-circle position-absolute top-50 translate-middle-y"
          />
          <span className="ms-4">{getNestedPropertyValue(errors, name)?.message}!</span>
         </>
        ) : (
         ''
        )}
       </Form.Text>
      </>
     }
    />
   </Form.Group>
  );
 }

 if (type === 'select') {
  return (
   <Form.Group {...formGroupProps}>
    {!!label && <Form.Label {...formLabelProps}>{Label}</Form.Label>}
    <Col
     {...formColumnProps}
     children={
      <>
       <Form.Select
        type={type}
        {...formControlProps}
        isInvalid={getNestedPropertyValue(errors, name)}
        isValid={Object.keys(errors).length > 0 && !getNestedPropertyValue(errors, name)}
       >
        <option value="">{placeholder}</option>
        {options.map((option, i) => (
         <option value={option.value} key={option?.value + i} disabled={!!option?.disabled}>
          {option.label}
         </option>
        ))}
       </Form.Select>
       {getNestedPropertyValue(errors, name)?.message && (
        <Form.Control.Feedback type="invalid">{getNestedPropertyValue(errors, name).message}</Form.Control.Feedback>
       )}
      </>
     }
    />
   </Form.Group>
  );
 }

 if (type === 'textarea') {
  return (
   <Form.Group {...formGroupProps}>
    {!!label && <Form.Label {...formLabelProps}>{Label}</Form.Label>}
    <Col
     {...formColumnProps}
     children={
      <>
       <Form.Control
        as="textarea"
        placeholder={placeholder}
        rows={formControlProps?.rows || 9}
        {...formControlProps}
        isValid={Object.keys(errors).length > 0 && !getNestedPropertyValue(errors, name)}
        isInvalid={getNestedPropertyValue(errors, name)}
       />
       {getNestedPropertyValue(errors, name)?.message && (
        <Form.Control.Feedback type="invalid">{getNestedPropertyValue(errors, name).message}</Form.Control.Feedback>
       )}
      </>
     }
    />
   </Form.Group>
  );
 }

 if (type.split('-')[1] === 'floating') {
  const id = uuid();
  return (
   <Form.Group {...formGroupProps}>
    <Form.Floating>
     <Form.Control
      id={id}
      type={type.split('-')[0]}
      placeholder={placeholder}
      {...formControlProps}
      isInvalid={getNestedPropertyValue(errors, name)}
      isValid={Object.keys(errors).length > 0 && !getNestedPropertyValue(errors, name)}
     />
     <label htmlFor={id}>{label}</label>
    </Form.Floating>

    {getNestedPropertyValue(errors, name)?.message && (
     <Form.Control.Feedback type="invalid">{getNestedPropertyValue(errors, name).message}</Form.Control.Feedback>
    )}
   </Form.Group>
  );
 }

 if (type.split('-')[0] === 'simple') {
  return (
   <Form.Group {...formGroupProps}>
    <Form.Control
     type={type.split('-')[1]}
     placeholder={placeholder}
     {...formControlProps}
     isInvalid={getNestedPropertyValue(errors, name)}
     isValid={Object.keys(errors).length > 0 && !getNestedPropertyValue(errors, name)}
    />
    {getNestedPropertyValue(errors, name)?.message && (
     <Form.Control.Feedback type="invalid">{getNestedPropertyValue(errors, name).message}</Form.Control.Feedback>
    )}
   </Form.Group>
  );
 }

 if (type === 'phone') {
  return (
   <Form.Group {...formGroupProps}>
    {!!label && <Form.Label {...formLabelProps}>{Label}</Form.Label>}
    <Col
     {...formColumnProps}
     children={
      <PhoneInput
       international
       countryCallingCodeEditable={false}
       defaultCountry="CO"
       placeholder={placeholder}
       {...formControlProps}
      />
     }
    />
   </Form.Group>
  );
 }

 if (type === 'tel') {
  return (
   <Form.Group {...formGroupProps}>
    {!!label && <Form.Label {...formLabelProps}>{Label}</Form.Label>}
    <Col
     {...formColumnProps}
     children={
      <Input
       international
       placeholder={placeholder}
       {...formControlProps}
       inputComponent={CustomInput}
       errorMessage={getNestedPropertyValue(errors, name)?.message}
       isInvalid={!!getNestedPropertyValue(errors, name)}
       isValid={Object.keys(errors).length > 0 && !getNestedPropertyValue(errors, name)}
      />
     }
    />
   </Form.Group>
  );
 }

 if (type === 'list') {
  const { onChange, top, style, register, ...formControl } = formControlProps;

  /* useEffect(() => {
   setItems(options);
  }, [options]); */

  const InputList = (
   <Form.Group {...formGroupProps}>
    {!!label && <Form.Label {...formLabelProps}>{Label}</Form.Label>}
    <Col
     {...formColumnProps}
     children={
      <>
       <div className="position-relative">
        <Form.Control
         as="textarea"
         ref={itemRef}
         type="text"
         name={name}
         value={item?.value || ''}
         onChange={handleChange}
         placeholder={placeholder}
         {...formControl}
         //onKeyDown={onKeyDown}
         isInvalid={errors && errors[register?.name]}
        />
        <Form.Control type="hidden" {...register} />
        <div className="d-flex position-absolute end-0 top-50 translate-middle-y">
         <Button
          variant="link"
          type="button"
          onClick={() => onChange(handleSubmit())}
          className="flex-fill align-items-center fs-0 text-700 p-0 me-2"
          disabled={!item?.value}
         >
          <FontAwesomeIcon icon="arrow-right" />
         </Button>

         <Button
          variant="link"
          type="button"
          onClick={handleReset}
          className="flex-fill align-items-center fs-0 text-700 p-0 me-3"
          disabled={!item?.value}
         >
          <FontAwesomeIcon icon="times" />
         </Button>
        </div>
       </div>

       <Form.Text className="text-danger position-relative">
        {errors && errors[register?.name] ? (
         <>
          <FontAwesomeIcon
           icon="exclamation-circle"
           className="text-white fs-1 bg-warning rounded-circle position-absolute top-50 translate-middle-y"
          />
          <span className="ms-4">{errors[register?.name]?.message}!</span>
         </>
        ) : (
         ''
        )}
       </Form.Text>
      </>
     }
    />
   </Form.Group>
  );

  if (type === 'listSocial') {
   const { onChange, top, ...formControl } = formControlProps;
   const sociaList = (
    <Form.Group {...formGroupProps}>
     <Form.Label {...formLabelProps}>{label}</Form.Label>
     <Col
      {...formColumnProps}
      children={
       <div className="position-relative">
        <Form.Control
         ref={itemRef}
         type="text"
         name={name}
         value={item?.value || ''}
         onChange={handleChange}
         placeholder={placeholder}
         {...formControl}
        />
        <div className="d-flex position-absolute end-0 top-50 translate-middle-y">
         <Button
          variant="link"
          type="button"
          onClick={() => onChange(handleSubmit())}
          className="flex-fill align-items-center fs-0 text-700 p-0 me-2"
          disabled={!item?.value}
         >
          {' '}
          <FontAwesomeIcon icon="faFacebook" />
         </Button>
         <Button
          variant="link"
          type="button"
          onClick={() => onChange(handleSubmit())}
          className="flex-fill align-items-center fs-0 text-700 p-0 me-2"
          disabled={!item?.value}
         >
          <FontAwesomeIcon icon="arrow-right" />
         </Button>
         <Button
          variant="link"
          type="button"
          onClick={handleReset}
          className="flex-fill align-items-center fs-0 text-700 p-0 me-3"
          disabled={!item?.value}
         >
          <FontAwesomeIcon icon="times" />
         </Button>
        </div>
        <Form.Control.Feedback type="invalid">{getNestedPropertyValue(errors, name)?.message}</Form.Control.Feedback>
       </div>
      }
     />
    </Form.Group>
   );
   return (
    <ul className="list-unstyled">
     {top && sociaList}
     {options.map(itm => (
      <li key={itm.id}>
       <Flex
        alignItems="center"
        className={classNames('hover-actions-trigger py-3 border-300', {
         'border-bottom': top,
         'border-top': !top
        })}
       >
        <FontAwesomeIcon icon="grip-lines" className="fs--2 text-secondary me-3" />

        <p className="fs--1 mb-0 me-6"> {itm.value}</p>
        <div className="hover-actions end-0 top-50 translate-middle-y">
         <OverlayTrigger overlay={<Tooltip>Editar</Tooltip>}>
          <Button
           variant="falcon-default"
           type="button"
           onClick={() => setItem(itm)}
           className="icon-item icon-item-sm rounded-circle fs--1 me-1"
          >
           <FontAwesomeIcon icon="pencil-alt" />
          </Button>
         </OverlayTrigger>
         <OverlayTrigger overlay={<Tooltip> Remove </Tooltip>}>
          <Button
           variant="falcon-default"
           type="button"
           onClick={() => onChange(options.filter(({ id }) => id !== itm.id))}
           className="icon-item icon-item-sm rounded-circle fs--1"
          >
           <FontAwesomeIcon icon="times" />
          </Button>
         </OverlayTrigger>
        </div>
       </Flex>
      </li>
     ))}
     {!top && sociaList}
    </ul>
   );
  }

  return (
   <ul className="list-unstyled m-0">
    {/* ---------------agregar a la lista input Terminos top--------- */}
    {top && InputList}
    {/* --------------------------END-------------------------------- */}

    {/* ----agregar estilos height o maxHeight para hacer Scroll----- */}
    <SimpleBarReact style={style} className="pe-3">
     {/* --------------------array de lista------------------------- */}
     {options?.map(itm => (
      <li key={itm.id}>
       <Flex
        alignItems="center"
        className={classNames('hover-actions-trigger py-2 border-300', {
         'border-bottom': top,
         'border-top': !top
        })}
       >
        <FontAwesomeIcon icon="grip-lines" className="fs--2 text-secondary me-3" />
        <p className="fs--1 mb-0 me-6">{itm.value}</p>
        <div className="hover-actions end-0 top-50 translate-middle-y">
         <OverlayTrigger overlay={<Tooltip>Editar</Tooltip>}>
          <Button
           variant="link"
           type="button"
           onClick={() => setItem(itm)}
           className="d-flex align-items-center fs--1 text-700 p-0 me-1"
          >
           <FontAwesomeIcon icon="pencil-alt" />
          </Button>
         </OverlayTrigger>
         <OverlayTrigger overlay={<Tooltip> Remove </Tooltip>}>
          <Button
           variant="falcon-default"
           type="button"
           onClick={() => onChange(options.filter(({ id }) => id !== itm.id))}
           className="icon-item icon-item-sm rounded-circle fs--1"
          >
           <FontAwesomeIcon icon="times" />
          </Button>
         </OverlayTrigger>
        </div>
       </Flex>
      </li>
     ))}
     {/* --------------------------END------------------------------ */}
    </SimpleBarReact>
    {/* --------------------------END-------------------------------- */}

    {/* -----------=agregar a la lista input Terminos bottom--------- */}
    {!top && InputList}
    {/* --------------------------END-------------------------------- */}
   </ul>
  );
 }

 if (type === 'hidden') {
  return <Form.Control type="hidden" {...formControlProps} />;
 }

 if (type === 'hiddenInput') {
  return (
   <>
    <Form.Control type="hidden" {...formControlProps} />
    {errors && getNestedPropertyValue(errors, name) && (
     <Alert variant="danger" className="m-0 py-2">
      <Alert.Heading className="fs-1 mb-0">{label}</Alert.Heading>
      <p className="mb-0">{getNestedPropertyValue(errors, name).message}</p>
     </Alert>
    )}
   </>
  );
 }

 if (type === 'range') {
  return (
   <Form.Group {...formGroupProps}>
    {!!label && <Form.Label {...formLabelProps}>{Label}</Form.Label>}
    <Col
     {...formColumnProps}
     children={
      <>
       <Form.Range {...formControlProps} />
       {getNestedPropertyValue(errors, name)?.message && (
        <Form.Control.Feedback type="invalid">{getNestedPropertyValue(errors, name).message}</Form.Control.Feedback>
       )}
      </>
     }
    />
   </Form.Group>
  );
 }

 if (type === 'range2') {
  return (
   <Form.Group {...formGroupProps}>
    {!!label && <Form.Label {...formLabelProps}>{Label}</Form.Label>}
    <Col
     {...formColumnProps}
     children={
      <>
       <FalconReactRange {...formControlProps} />
       {getNestedPropertyValue(errors, name)?.message && (
        <Form.Control.Feedback type="invalid">{getNestedPropertyValue(errors, name).message}</Form.Control.Feedback>
       )}
      </>
     }
    />
   </Form.Group>
  );
 }

 if (type === 'color') {
  return (
   <Form.Group {...formGroupProps}>
    {!!label && <Form.Label {...formLabelProps}>{Label}</Form.Label>}
    <Col
     {...formColumnProps}
     children={
      <>
       <Form.Control
        type={type}
        {...formControlProps}
        isInvalid={getNestedPropertyValue(errors, name)}
        isValid={Object.keys(errors).length > 0 && !getNestedPropertyValue(errors, name)}
       />
       {getNestedPropertyValue(errors, name)?.message && (
        <Form.Control.Feedback type="invalid">{getNestedPropertyValue(errors, name).message}</Form.Control.Feedback>
       )}
      </>
     }
    />
   </Form.Group>
  );
 }

 return (
  <Form.Group {...formGroupProps}>
   {!!label && <Form.Label {...formLabelProps}>{Label}</Form.Label>}
   <Col
    {...formColumnProps}
    children={
     <>
      <Form.Control
       type={type}
       placeholder={placeholder}
       {...formControlProps}
       isInvalid={getNestedPropertyValue(errors, name)}
       isValid={Object.keys(errors).length > 0 && !getNestedPropertyValue(errors, name)}
      />
      {getNestedPropertyValue(errors, name)?.message && (
       <Form.Control.Feedback type="invalid">{getNestedPropertyValue(errors, name).message}</Form.Control.Feedback>
      )}
     </>
    }
   />
  </Form.Group>
 );
};

CustomInput.propTypes = {
 value: PropTypes.string,
 onChange: PropTypes.func,
 isInvalid: PropTypes.bool,
 isValid: PropTypes.bool,
 formControlProps: PropTypes.object,
 size: PropTypes.string,
 errorMessage: PropTypes.string
};

CustomDateInput.propTypes = {
 value: PropTypes.string,
 onClick: PropTypes.func,
 isInvalid: PropTypes.bool,
 isValid: PropTypes.bool,
 formControlProps: PropTypes.object,
 errorMessage: PropTypes.string
};

Inputs.propTypes = {
 help: PropTypes.object,
 label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
 id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
 name: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
 errors: PropTypes.object,
 type: PropTypes.string.isRequired,
 options: PropTypes.array,
 placeholder: PropTypes.string,
 formControlProps: PropTypes.object,
 formGroupProps: PropTypes.object,
 formLabelProps: PropTypes.object,
 formColumnProps: PropTypes.object,
 datepickerProps: PropTypes.object
};

Inputs.defaultProps = { errors: {} };

export default Inputs;
